import {
	Box,
	Button,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Modal,
	OutlinedInput,
	Select,
	SelectChangeEvent,
	Typography,
} from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react";
import { useBus, useListener } from "react-bus";
import UserApi from "../../../../api/user";
import { AppContext, IAppContext } from "../../../../Contexts/AppContext";
import { UserRole, UserStatus } from "../../../../models/User";
import { getUserRoleForPermissionary } from "../../../../utils/userPermissionaries";
import { EditUserModalProps } from "./types";

const style = {
	position: "absolute" as "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: 400,
	bgcolor: "background.paper",
	border: "2px solid #000",
	boxShadow: 24,
	p: 4,
};

export default function EditUserModal(props: EditUserModalProps) {
	const { userToEdit } = props;
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [user, setUser] = useState({
		email: "",
		status: UserStatus.ACTIVE,
		role: UserRole.USER,
	});
	const bus = useBus();
	const { snackBar, setSnackBar, userContext } = useContext(
		AppContext
	) as IAppContext;

	const handleOpen = useCallback(() => setOpen(true), []);
	const handleClose = () => setOpen(false);

	useListener("openEditUserModal", handleOpen);

	useEffect(() => {
		if (!userToEdit || !userContext?.permissionaryId) {
			return;
		}

		setUser({
			email: userToEdit.email,
			status: userToEdit.status,
			role: getUserRoleForPermissionary(userToEdit, userContext.permissionaryId),
		});
	}, [userContext?.permissionaryId, userToEdit]);

	const update = useCallback(async () => {
		if (!userToEdit || !userContext?.permissionaryId || user.email.trim() === "") {
			return;
		}

		try {
			setLoading(true);
			await UserApi.updateUser({
				token: localStorage.getItem("accessToken") || "",
				userId: userToEdit.id || userToEdit._id,
				user: {
						email: user.email.trim().toLowerCase(),
						status: user.status,
						role: user.role,
						permissionaryId: userContext.permissionaryId,
					},
				});

			bus.emit("reloadUsersScreenData");
			handleClose();
			setSnackBar({
				...snackBar,
				open: true,
				message: "Usuario actualizado",
				autoHideDuration: 6000,
				severity: "success",
			});
		} catch (error: any) {
			console.error(error);
			setSnackBar({
				...snackBar,
				open: true,
				message: "Error al actualizar el usuario",
				autoHideDuration: 6000,
				severity: "error",
			});
		} finally {
			setLoading(false);
		}
	}, [bus, setSnackBar, snackBar, user, userContext?.permissionaryId, userToEdit]);

	return (
		<Modal
			open={open}
			onClose={handleClose}
			aria-labelledby='edit-user-modal-title'
			aria-describedby='edit-user-modal-description'>
			<Box sx={style}>
				<Typography
					textAlign='center'
					id='edit-user-modal-title'
					variant='h6'
					component='h2'>
					Editar Usuario
				</Typography>
				<div style={{ marginBottom: "15px", width: "100%" }}>
					<FormControl sx={{ m: 1, width: "90%" }} variant='outlined'>
						<InputLabel htmlFor='edit-user-email'>Email</InputLabel>
						<OutlinedInput
							id='edit-user-email'
							type='text'
							placeholder='Email'
							label='Email'
							value={user.email}
							onChange={(event) => {
								setUser({
									...user,
									email: event.target.value.toLowerCase(),
								});
							}}
							onKeyUp={(event) => {
								if (event.key === "Enter") {
									update();
								}
							}}
						/>
					</FormControl>
				</div>
				<div style={{ marginBottom: "15px", width: "100%" }}>
					<FormControl sx={{ m: 1, width: "90%" }} variant='outlined'>
						<InputLabel id='edit-user-status-label'>Status</InputLabel>
						<Select
							labelId='edit-user-status-label'
							id='edit-user-status'
							value={user.status}
							label='Status'
							onChange={(event: SelectChangeEvent) => {
								setUser({
									...user,
									status: event.target.value as UserStatus,
								});
							}}>
							<MenuItem value={UserStatus.ACTIVE}>Activo</MenuItem>
							<MenuItem value={UserStatus.INACTIVE}>Inactivo</MenuItem>
						</Select>
					</FormControl>
				</div>
				<div style={{ marginBottom: "15px", width: "100%" }}>
					<FormControl sx={{ m: 1, width: "90%" }} variant='outlined'>
							<InputLabel id='edit-user-role-label'>
								Rol en {userContext?.permissionaryName || "permisionario"}
							</InputLabel>
						<Select
							labelId='edit-user-role-label'
							id='edit-user-role'
							value={user.role}
								label={`Rol en ${
									userContext?.permissionaryName || "permisionario"
								}`}
							onChange={(event: SelectChangeEvent) => {
								setUser({
									...user,
									role: event.target.value as UserRole,
								});
							}}>
							<MenuItem value={UserRole.ADMIN}>Administrador</MenuItem>
							<MenuItem value={UserRole.USER}>Usuario</MenuItem>
						</Select>
					</FormControl>
				</div>
				<div style={{ marginTop: "10px", textAlign: "center" }}>
					{loading ? (
						<CircularProgress size={30} />
					) : (
						<Button
							disabled={user.email.trim() === ""}
							variant='contained'
							color='primary'
							sx={{
								width: "40%",
								marginTop: 2,
							}}
							onClick={() => update()}>
							Guardar
						</Button>
					)}
				</div>
			</Box>
		</Modal>
	);
}
